'use client';

import { useRef } from 'react';
import { parseAsString, useQueryStates } from 'nuqs';

import { CallInsights } from '@/app/(app)/analytics/call-analytics/[id]/components/call-insights';
import { PageHeader, PageMain } from '@/app/(app)/page-main';
import { useApiV1Ctx } from '@/components/providers/v1-api';
import { Skeleton } from '@/components/ui/skeleton';
import { useLocationParam } from '@/lib/router';

import { ANALYTICS_AI_COACH_PAGE, ANALYTICS_CALLS_DASHBOARD_PAGE } from '../../nav';
import { Summary, Transcript } from './components/call-asr';
import { CallAudio } from './components/call-audio';
import { CallMetrics } from './components/call-metrics';

const backwardRefMap: Record<'calls' | string, { label: string; href: string }> = {
  calls: { label: ANALYTICS_CALLS_DASHBOARD_PAGE.label, href: ANALYTICS_CALLS_DASHBOARD_PAGE.url },
  dashboard: { label: ANALYTICS_AI_COACH_PAGE.label, href: ANALYTICS_AI_COACH_PAGE.url },
};

export function CallAnalyticsPage() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const id = useLocationParam('id');
  const [{ return_to }] = useQueryStates({
    return_to: parseAsString.withDefault(''),
  });

  const returnTo = return_to && backwardRefMap[return_to] ? backwardRefMap[return_to] : null;
  const breadcrumbs = returnTo ? returnTo : backwardRefMap.calls;

  const { api } = useApiV1Ctx();

  const { data, isLoading } = api.externalCalls.getByIdExternalCallsExternalCallIdGet.useQuery({
    path: { external_call_id: id },
  });

  const callType = `${(data?.call_type || '').charAt(0).toUpperCase() + (data?.call_type || '').slice(1)}`;
  const title = `${callType} Call for ${data?.caller_phone_number ? data.caller_phone_number : 'Unknown'}`;
  const recording = data?.recordings?.[0];

  if (isLoading) {
    return (
      <PageMain title='Calls Analytics'>
        <div>
          <Skeleton className='h-[80dvh] w-full' />
        </div>
      </PageMain>
    );
  }

  if (!data) {
    return <div>No data</div>;
  }

  return (
    <div className='flex h-[calc(100vh-1rem)] w-full flex-col overflow-hidden'>
      <PageHeader title={title} breadcrumbs={[breadcrumbs]} />
      <div className='mt-3 flex h-full w-full flex-1 overflow-y-hidden border-t'>
        <div className='flex w-full flex-col overflow-y-hidden'>
          <div className='space-y-6 overflow-y-auto p-6'>
            {recording && <Summary recording={recording} />}
            <CallInsights call={data} />
            {data.metrics && <CallMetrics callId={id} metrics={data.metrics} />}
          </div>
        </div>
        <div className='min-h-full w-1/2 min-w-1/2 space-y-6 overflow-y-auto border-l py-6 xl:w-1/3 xl:min-w-1/3'>
          {recording && (
            <>
              <div className='px-6'>
                <CallAudio ref={audioRef} callId={id} callRecordingId={recording.id} />
              </div>
              <Transcript
                recording={recording}
                onClickStartAt={(start) => {
                  if (audioRef.current) {
                    audioRef.current.currentTime = start;
                    audioRef.current.play();
                  }
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
